import type { Context } from 'hono'
import type {
  ModuleContext,
  ModuleManifest,
  PluginManifest,
  ActionDefinition,
  PageDefinition,
  PageDefinitionDTO,
  ModuleDTO,
  PluginDTO,
  ManifestDTO,
  CombinedManifestDTO
} from '@gzl10/nexus-sdk'
import type { NexusEnv } from '../../http/types.js'
import { toEntityDefinitionDTO, toActionDTO, moduleHasSeedWithPath } from './system.helpers.js'

type C = Context<NexusEnv>

/**
 * Converts PageDefinition to a serializable DTO (no functions)
 */
function toPageDTO(page: PageDefinition): PageDefinitionDTO {
  return {
    id: page.id,
    label: page.label,
    icon: page.icon,
    type: page.type,
    caslSubject: page.caslSubject,
    order: page.order,
    hidden: page.hidden,
    standalone: page.standalone,
    dataSource: page.dataSource,
    widgets: page.widgets,
    component: page.component,
    contentEndpoint: page.contentEndpoint,
    meta: page.meta,
    layout: page.layout
  }
}

export function createSystemController(ctx: ModuleContext) {
  const { core, engine } = ctx
  const errors = core.errors

  function toModuleDTO(mod: ModuleManifest, libPath: string): ModuleDTO {
    return {
      name: mod.name,
      label: mod.label!,
      icon: mod.icon,
      description: mod.description,
      type: mod.type ?? 'core',
      category: mod.category,
      dependencies: mod.dependencies ?? [],
      routePrefix: mod.routePrefix ?? `/${mod.name}`,
      subjects: engine.getModuleSubjects(mod),
      definitions: (mod.definitions ?? [])
        .filter(def => ('expose' in def ? (def as { expose?: boolean }).expose : true) !== false)
        .map((def) => {
          const baseDTO = toEntityDefinitionDTO(def as unknown as Record<string, unknown>, engine, mod.name)

          const defActions = 'actions' in def && Array.isArray((def as { actions?: unknown }).actions)
            ? ((def as { actions: ActionDefinition[] }).actions).filter(a => !a.hidden)
            : []

          return {
            ...baseDTO,
            actions: defActions.length > 0
              ? defActions.map(action => toActionDTO(action, undefined, ctx))
              : undefined
          }
        }),
      actions: (mod.actions ?? []).length > 0
        ? (mod.actions ?? []).filter(a => !a.hidden).map(action => toActionDTO(action, undefined, ctx))
        : undefined,
      pages: (mod.pages ?? []).length > 0
        ? (mod.pages ?? []).map(toPageDTO)
        : undefined,
      hasRoutes: !!mod.routes || (mod.definitions?.length ?? 0) > 0 || (mod.pages?.length ?? 0) > 0 || (mod.actions?.length ?? 0) > 0,
      hasMigrate: !!mod.migrate,
      hasSeed: moduleHasSeedWithPath(mod, libPath),
      hasInit: !!mod.init
    }
  }

  function toPluginDTO(plugin: PluginManifest, libPath: string): PluginDTO {
    const state = ctx.core.plugins.getState(plugin.name)
    return {
      name: plugin.name,
      code: plugin.code,
      label: plugin.label,
      icon: plugin.icon,
      category: plugin.category,
      version: plugin.version,
      description: plugin.description,
      installed: true,
      enabled: state?.enabled ?? null,
      modules: plugin.modules.map(mod => toModuleDTO(mod, libPath)),
      envVars: plugin.envVars,
      peerDependencies: plugin.peerDependencies,
      setup: plugin.setup ? {
        steps: plugin.setup.steps,
        docsUrl: plugin.setup.docsUrl,
        prerequisites: plugin.setup.prerequisites,
        caveats: plugin.setup.caveats
      } : undefined
    }
  }

  function toManifestDTO(manifest: { name: string; version: string; modules: ModuleManifest[]; plugins?: PluginManifest[] }): ManifestDTO {
    const libPath = ctx.core.getLibPath()
    return {
      name: manifest.name,
      version: manifest.version,
      modules: manifest.modules.map(mod => toModuleDTO(mod, libPath)),
      plugins: manifest.plugins?.map(p => toPluginDTO(p, libPath))
    }
  }

  return {
    /**
     * GET /system/modules
     */
    async listModules(c: C): Promise<Response> {
      const libPath = ctx.core.getLibPath()
      const source = c.req.query('source') ?? 'all'
      const sort = c.req.query('sort')
      const order = (c.req.query('order') as 'asc' | 'desc' | undefined) ?? 'asc'

      let filters: Record<string, unknown> | undefined
      const rawFilters = c.req.query('filters')
      if (rawFilters) {
        try {
          filters = JSON.parse(rawFilters) as Record<string, unknown>
        } catch {
          // ignore
        }
      }

      let modules: ModuleManifest[]
      if (source === 'core') {
        modules = engine.getCoreModules()
      } else if (source === 'user') {
        modules = engine.getUserModules()
      } else {
        modules = engine.getModules()
      }

      let items = modules.map(mod => toModuleDTO(mod, libPath))

      if (filters && Object.keys(filters).length > 0) {
        items = items.filter(item => {
          const record = item as unknown as Record<string, unknown>
          for (const [key, value] of Object.entries(filters!)) {
            if (value === undefined || value === null || value === '') continue
            const itemValue = record[key]
            if (Array.isArray(value) && value.length > 0) {
              if (!value.includes(itemValue)) return false
            } else if (itemValue !== value) {
              return false
            }
          }
          return true
        })
      }

      if (sort) {
        items = items.sort((a, b) => {
          const aRecord = a as unknown as Record<string, unknown>
          const bRecord = b as unknown as Record<string, unknown>
          const aVal = aRecord[sort]
          const bVal = bRecord[sort]
          if (String(aVal ?? '') < String(bVal ?? '')) return order === 'asc' ? -1 : 1
          if (String(aVal ?? '') > String(bVal ?? '')) return order === 'asc' ? 1 : -1
          return 0
        })
      }

      return c.json({
        items,
        total: items.length,
        page: 1,
        limit: items.length,
        totalPages: 1,
        hasNext: false
      })
    },

    /**
     * GET /system/modules/:name
     */
    async getModule(c: C): Promise<Response> {
      const name = c.req.param('name')
      const mod = engine.getModules().find((m) => m.name === name)
      if (!mod) {
        throw new errors.NotFoundError('Module')
      }
      return c.json(toModuleDTO(mod, ctx.core.getLibPath()))
    },

    /**
     * GET /system/plugins
     */
    async listPlugins(c: C): Promise<Response> {
      const libPath = ctx.core.getLibPath()
      const plugins = engine.getPlugins()
      const items = plugins.map(p => toPluginDTO(p, libPath))
      return c.json({
        items,
        total: items.length,
        page: 1,
        limit: items.length,
        totalPages: 1,
        hasNext: false
      })
    },

    /**
     * GET /system/plugin?name=xxx
     */
    async getPlugin(c: C): Promise<Response> {
      const name = c.req.query('name')
      if (!name) {
        throw new errors.ValidationError('name query parameter is required')
      }
      const plugin = engine.getPlugins().find(p => p.name === name)
      if (!plugin) {
        throw new errors.NotFoundError('Plugin')
      }
      return c.json(toPluginDTO(plugin, ctx.core.getLibPath()))
    },

    /**
     * GET /system/manifest
     */
    async getManifest(c: C): Promise<Response> {
      const coreManifest = engine.getCoreManifest()
      const userManifest = engine.getUserManifest()

      const result: CombinedManifestDTO = {
        core: toManifestDTO(coreManifest),
        user: userManifest ? toManifestDTO(userManifest) : null,
        hasUserApp: engine.hasUserApp(),
        tenantId: ctx.tenantId
      }
      return c.json(result)
    },

    /**
     * GET /system/manifest/core
     */
    async getCoreManifest(c: C): Promise<Response> {
      const manifest = engine.getCoreManifest()
      return c.json(toManifestDTO(manifest))
    },

    /**
     * GET /system/manifest/user
     */
    async getUserManifest(c: C): Promise<Response> {
      const manifest = engine.getUserManifest()
      if (!manifest) {
        return c.json(null)
      }
      return c.json(toManifestDTO(manifest))
    },

    /**
     * GET /system/capabilities
     * Public — no auth required.
     */
    async getCapabilities(c: C): Promise<Response> {
      try {
        c.header('Cache-Control', 'public, max-age=300')
        const body = await ctx.capabilities.resolve()
        return c.json(body as Record<string, unknown>)
      } catch (err) {
        ctx.core.logger.error({ err }, 'Failed to resolve capabilities')
        c.header('Cache-Control', 'no-cache')
        return c.json(
          { error: { code: 'INTERNAL_ERROR', message: 'Failed to resolve capabilities' } },
          500
        )
      }
    }
  }
}
